import { TProxyPlanKey } from "@/components/newcustomize/hooks/useProxyPlanKey";
import { useMemo } from "react";
import makeDefaultVerifiedProxyCount from "@/components/newcustomize/utils/makeDefaultVerifiedProxyCount";
import useCustomize from "@/components/newcustomize/hooks/useCustomize";
import useIsFlagVariantTest from "@/hooks/useIsFlagVariantTest";
import useFlagsSettled from "@/hooks/useFlagsSettled";

/** A plan's preset counts, with which of them is the default and which is tagged popular. */
function counts(
  values: number[],
  {
    defaultCount,
    popularCount,
  }: { defaultCount: number; popularCount?: number }
): TProxyCount[] {
  return values.map((proxyCount) => ({
    proxyCount,
    ...(proxyCount === defaultCount && { isDefault: true }),
    ...(proxyCount === popularCount && { isPopular: true }),
  }));
}

/**
 * The eight most purchased counts per plan (2026 purchase data); the most
 * popular of each group carries the tag. Shared and Private ISP start where
 * their lists started before the redesign (20 and 25): the cheaper entry
 * cards cannibalised new revenue, and a smaller amount is still purchasable
 * through the custom amount card (R-2252).
 */
const hardcodedDefaults: Record<TProxyPlanKey, TProxyCount[]> = {
  shared_default: counts([100, 250, 500, 1000, 2500, 5000, 10000, 15000], {
    defaultCount: 1000,
    popularCount: 1000,
  }),
  semidedicated_premium: counts([25, 50, 100, 250, 500, 1000, 5000, 15000], {
    defaultCount: 50,
    popularCount: 25,
  }),
  dedicated_premium: counts([20, 50, 75, 100, 1000, 2000, 5000, 10000], {
    defaultCount: 75,
    popularCount: 20,
  }),
  shared_isp: counts([20, 50, 75, 100, 250, 500, 1000, 7000], {
    defaultCount: 100,
    popularCount: 20,
  }),
  semidedicated_isp: counts([25, 50, 100, 250, 500, 1000, 2000, 5000], {
    defaultCount: 50,
    popularCount: 100,
  }),
  dedicated_isp: counts([1, 10, 20, 50, 75, 100, 250, 2000], {
    defaultCount: 75,
    popularCount: 10,
  }),
  shared_residential: [
    {
      proxyCount: 80000000,
      isDefault: true, //0 is default
      title: "Up to 80 million IPs/month",
      description: "Standard", //TODO: Make sure description is no longer used and remove it.
    },
  ],
  shared_verified: [{ proxyCount: makeDefaultVerifiedProxyCount() }],
};

/** R-2121 test arm: 75 and 2000 swapped for 500 and 1000. */
const dedicatedIspExperimentCounts = counts(
  [1, 10, 20, 50, 100, 250, 500, 1000],
  { defaultCount: 50, popularCount: 10 }
);

export type TProxyCount = {
  proxyCount: number;
  isDefault?: boolean;
  isPopular?: boolean;
  title?: string;
  description?: string;
};

// This is a hook because we would like to edit it on the go with feature flags
export default function useProxyCount({
  onlyProxyPlan,
}: {
  /**
   * In some cases we do not want to make requests for all proxy type combinations
   * this allows us to limit what we are interested in
   */
  onlyProxyPlan: TProxyPlanKey;
}) {
  const isDedicatedIspExperiment = useIsFlagVariantTest(
    "dedicated_isp_proxy_count_cards"
  );
  // The URL defaults are written once, so a default read before the R-2121 flag
  // lands would put control's 75 onto the test arm's cards, where no card has it.
  const flagsSettled = useFlagsSettled();
  const isWaitingForFlags = onlyProxyPlan === "dedicated_isp" && !flagsSettled;

  const mapping = useMemo(
    () =>
      onlyProxyPlan === "dedicated_isp" && isDedicatedIspExperiment
        ? dedicatedIspExperimentCounts
        : hardcodedDefaults[onlyProxyPlan] || [],
    [onlyProxyPlan, isDedicatedIspExperiment]
  );

  const plansCustomizeResponse = useCustomize();

  const defaultsMapping = useMemo(() => {
    const { proxy_count_max } = plansCustomizeResponse.data || {};

    if (typeof proxy_count_max === "undefined" || isWaitingForFlags) {
      return undefined;
    }

    // Find the default, but limit it to maximum value returned by the backend
    return mapping.find((item, index) => {
      // Limit to the max stock value
      if (index + 1 < mapping.length) {
        const nextProxyCount = mapping[index + 1].proxyCount;

        if (nextProxyCount > proxy_count_max) {
          return true;
        }
      }

      return item.isDefault;
    })?.proxyCount;
  }, [mapping, plansCustomizeResponse, isWaitingForFlags]);

  return {
    mapping,
    defaultsMapping,
    isLoading: plansCustomizeResponse.isLoading || isWaitingForFlags,
  };
}
